/** @jsxImportSource @emotion/react */
import NotificationsIcon from "@mui/icons-material/Notifications";
import Badge from "@mui/material/Badge";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import Paper from "@mui/material/Paper";
import Popover from "@mui/material/Popover";
import { makeStyles } from "@mui/styles";
import React, { useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { deleteNotifications, fetchNotifications } from "../api/notification";
import { fetchTimeOffRequestSnapshotByTorId } from "../api/timeOffRequest";
import { fetchTradeDataByTradeFormId } from "../api/trade";
import { selectUserSession } from "../slices/userSession/userSessionSlice";
import { selectWebSocketEventData } from "../slices/webSocketEvent/webSocketEventSlice";
import {
  CLEAR_ALL_NOTIFICATIONS,
  TIME_OFF_PAGE_TYPE,
  TRADE_PAGE_TYPE,
} from "../util/constants";
import { getScrubbedTimeOffRequestSnapshot } from "../util/timeOffRequestScrubber";
import { getScrubbedTradeFormEvent } from "../util/tradeFormEventScrubber";
import { mergeNotifications } from "../util/webSocketUtils";
import StarsCloseIconButton from "./fundamentals/StarsCloseIconButton";
import Notification from "./Notification";
import TimeOffRequestDialog from "./TimeOffRequestDialog";
import TradeDialog from "./TradeDialog.jsx";

const useStyles = makeStyles((theme) => ({
  clearButtonIcon: {
    marginTop: "-20px",
    marginRight: "-10px",
    color: "inherit",
  },
  badge: {
    backgroundColor: theme.palette.primary.swaRed,
  },
  noNotificationsMessage: {
    paddingBottom: "20px",
  },
}));

const NotificationsPanel = (props) => {
  const classes = useStyles();
  const userSession = useSelector(selectUserSession);
  const webSocketEventData = useSelector(selectWebSocketEventData);
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [notifications, setNotifications] = React.useState([]);
  const [isNotificationsLoading, setIsNotificationsLoading] = React.useState(true);
  const notificationWebSocketDataRef = useRef({});

  // Notification Dialog
  const [notificationData, setNotificationData] = React.useState(null);
  const [isNotificationDataLoading, setIsNotificationDataLoading] = React.useState(true);
  const [isDialogOpen, setIsDialogOpen] = React.useState(false);

  useEffect(() => {
    fetchNotifications(userSession.eid)
      .then((res) => res.json())
      .then((result) => {
        setNotifications(mergeNotifications(notificationWebSocketDataRef.current, result.notifications));
        notificationWebSocketDataRef.current = {};
        setIsNotificationsLoading(false);
      })
      .catch((err) => {
        console.error("error happened!", err);
        setIsNotificationsLoading(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    notificationWebSocketDataRef.current = webSocketEventData.notificationEvents;
    if (Object.values(webSocketEventData.notificationEvents).length > 0) {
      setNotifications((prevState) => {
        return mergeNotifications(webSocketEventData.notificationEvents, prevState);
      });
      notificationWebSocketDataRef.current = {};
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [webSocketEventData.notificationEvents]);

  const deleteNotification = (notificationId) => {
    let newNotifications = notifications.filter((notification) => {
      return notification.id !== notificationId;
    });
    setNotifications(newNotifications);
  };

  const handleClickDialogOpen = () => {
    setIsDialogOpen(true);
  };

  const handleClickDialogClose = () => {
    setIsDialogOpen(false);
  };

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleNotificationClick = (notification) => {
    switch (notification.type) {
      case TIME_OFF_PAGE_TYPE:
        fetchTimeOffRequestSnapshotByTorId(notification.torId)
          .then((res) => res.json())
          .then((result) => {
            setAnchorEl(false);
            let scrubbedTorSnapshot = getScrubbedTimeOffRequestSnapshot(result.torSnapshotObject);
            setNotificationData(scrubbedTorSnapshot);
            setIsNotificationDataLoading(false);
            handleClickDialogOpen();
            deleteNotification(notification.id);
          })
          .catch((err) => {
            console.error("error happened!", err);
            setIsNotificationDataLoading(false);
          });
        break;
      case TRADE_PAGE_TYPE:
      default:
        // Since all current Trade notifications in the system will NOT have a 'type' associated,
        // we need to ensure the default case is handled with Trade functionality.
        fetchTradeDataByTradeFormId(notification.tradeFormId)
          .then((res) => res.json())
          .then((result) => {
            setAnchorEl(false);
            let scrubbedTradeFormEvent = getScrubbedTradeFormEvent(result.latestTradeFormEvent);
            setNotificationData(scrubbedTradeFormEvent);
            setIsNotificationDataLoading(false);
            handleClickDialogOpen();
            deleteNotification(notification.id);
          })
          .catch((err) => {
            console.error("error happened!", err);
            setIsNotificationDataLoading(false);
          });
        break;
    }
  };

  const handleClearAllClicked = (event) => {
    clearAllNotifications(event);
  };

  const clearAllNotifications = (event) => {
    setNotifications([]);
    deleteNotifications(userSession.eid, CLEAR_ALL_NOTIFICATIONS);
  };

  const open = Boolean(anchorEl);
  const id = open ? "simple-popover" : undefined;

  const renderNotifications = () => {
    return (
      <Paper elevation={0} sx={{ overflowY: "scroll", height: "35vh" }}>
        <List>
          {isNotificationsLoading ? (
            <CircularProgress />
          ) : (
            notifications.map((notification, idx) => (
              <div key={notification.id}>
                <Notification
                  loggedInUserData={userSession}
                  notification={notification}
                  onNotificationClick={handleNotificationClick}
                  onDeleteNotificationClick={deleteNotification}
                />
                {idx !== notifications.length - 1 ? <Divider /> : ""}
              </div>
            ))
          )}
        </List>
      </Paper>
    );
  };

  const renderNotificationDialog = () => {
    if (notificationData.torId) {
      return (
        <TimeOffRequestDialog
          timeOffRequest={notificationData}
          isDialogOpen={isDialogOpen}
          handleClickDialogOpen={handleClickDialogOpen.bind(this)}
          handleClickDialogClose={handleClickDialogClose.bind(this)}
          isAdminPage={false}
          isDesktopView={props.isDesktopView}
        />
      );
    } else if (notificationData.tradeFormId) {
      return (
        <TradeDialog
          loggedInUserData={userSession}
          trade={notificationData}
          isDialogOpen={isDialogOpen}
          handleClickDialogOpen={handleClickDialogOpen.bind(this)}
          handleClickDialogClose={handleClickDialogClose.bind(this)}
          isAdminPage={false}
          eid={userSession.eid}
          isDesktopView={props.isDesktopView}
        />
      );
    }
  };

  const renderPopover = () => {
    return (
      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        data-testid="notifications-popover"
      >
        <div className="notifications-popover">
          <ListItem className="notification-item">
            <div className="notification-title">Notifications</div>
            <span></span>
            <StarsCloseIconButton className={classes.clearButtonIcon} onClick={handleClose} />
          </ListItem>
          {notifications.length > 0 ? (
            <>
              {renderNotifications()}
              <ListItem className="notification-item">
                <span></span>
                <Button
                  className="notification-clear-button"
                  variant="outlined"
                  color="primary"
                  onClick={handleClearAllClicked}
                  role="button"
                  button
                  data-testid="notifications-clear-button"
                >
                  Clear All
                </Button>
              </ListItem>
            </>
          ) : (
            <ListItem className={`notification-item ${classes.noNotificationsMessage}`}>No New Notifications</ListItem>
          )}
        </div>
      </Popover>
    );
  };

  return (
    <div>
      <IconButton
        data-testid="notification-bell-icon-button"
        sx={{
          marginRight: "5px"
        }}
        color="inherit"
        edge="start"
        size="medium"
        aria-label="Menu"
        onClick={handleClick}
      >
        <Badge classes={{ badge: classes.badge }} badgeContent={notifications.length} overlap="rectangular">
          <NotificationsIcon />
        </Badge>
      </IconButton>
      {renderPopover()}
      {!isNotificationDataLoading && isDialogOpen ? renderNotificationDialog() : ""}
    </div>
  );
};

export default NotificationsPanel;
